import { call, put, takeEvery } from "redux-saga/effects";
import { showAlert } from "../../redux/slices/alerts";
import { safeErrorMessage } from "../../utils/errorUtils";
import * as duck from "./duck";
import * as requests from "./requests";

function* respond(callback, request, ...args) {
    try {
        const response = yield call(request, ...args);
        if (callback) callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        if (error?.response?.status !== 404) yield put(showAlert({ severity: "error", message }));
        if (callback) callback(null, message, error?.response?.data);
    }
}

const filenameFrom = (disposition) => {
    const header = String(disposition || "");
    const encoded = header.match(/filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/);
    if (encoded) {
        try {
            return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ""));
        } catch {
            return "";
        }
    }
    const plain = header.match(/filename\s*=\s*("([^"]*)"|[^;]+)/);
    return plain ? (plain[2] ?? plain[1]).trim() : "";
};

const blobErrorBody = async (data) => {
    if (!(data instanceof Blob)) return {};
    try {
        const body = JSON.parse(await data.text());
        return body && typeof body === "object" ? body : {};
    } catch {
        return {};
    }
};

function* handleDownloadReportFile(action) {
    const { siteId, reportId, part, clientId, callback } = action.payload || {};
    try {
        const response = yield call(requests.requestGetReportFile, siteId, reportId, part, clientId);
        if (callback) callback({ blob: response.data, filename: filenameFrom(response.headers?.["content-disposition"]) });
    } catch (error) {
        const body = yield call(blobErrorBody, error?.response?.data);
        const message = body.msg || body.message || body.error || safeErrorMessage(error);
        yield put(showAlert({ severity: "error", message }));
        if (callback) callback(null, message);
    }
}

const bind = (type, request, argsOf) =>
    takeEvery(type, function* (action) {
        const payload = action.payload || {};
        yield* respond(payload.callback, request, ...argsOf(payload));
    });

export default function* cwReportsSaga() {
    yield bind(duck.CW_GET_SITES_REQUEST, requests.requestGetSites, () => []);
    yield bind(duck.CW_GET_SITE_REQUEST, requests.requestGetSite, (p) => [p.siteId, p.clientId]);
    yield bind(duck.CW_GET_SITE_CHANGES_REQUEST, requests.requestGetSiteChanges, (p) => [p.siteId, p.page, p.perPage, p.clientId]);
    yield bind(duck.CW_GET_SITE_REPORTS_REQUEST, requests.requestGetSiteReports, (p) => [p.siteId, p.clientId]);
    yield bind(duck.CW_GET_EXPOSURES_REQUEST, requests.requestGetExposures, () => []);
    yield bind(duck.CW_GET_EXPOSURE_SUMMARY_REQUEST, requests.requestGetExposureSummary, () => []);
    yield takeEvery(duck.CW_DOWNLOAD_REPORT_FILE_REQUEST, handleDownloadReportFile);
    yield bind(duck.CW_GET_FLEET_REQUEST, requests.requestGetFleet, () => []);
    yield bind(duck.CW_GET_FLEET_EXPOSURES_REQUEST, requests.requestGetFleetExposures, (p) => [p.clientId]);
    yield bind(duck.CW_GET_SETTINGS_REQUEST, requests.requestGetSettings, () => []);
    yield bind(duck.CW_UPDATE_SETTINGS_REQUEST, requests.requestUpdateSettings, (p) => [p.settings]);
    yield bind(duck.CW_GET_SERVICE_ACCOUNTS_REQUEST, requests.requestGetServiceAccounts, () => []);
    yield bind(duck.CW_TEST_CONNECTION_REQUEST, requests.requestTestConnection, () => []);
    yield bind(duck.CW_GET_SITE_MAPPING_REQUEST, requests.requestGetSiteMapping, () => []);
    yield bind(duck.CW_GET_MAPPING_ACCOUNTS_REQUEST, requests.requestGetMappingAccounts, (p) => [p.clientId]);
    yield bind(duck.CW_UPDATE_SITE_MAPPING_REQUEST, requests.requestUpdateSiteMapping, (p) => [p.entries]);
    yield bind(duck.CW_UPDATE_CLIENT_SETTINGS_REQUEST, requests.requestUpdateClientSettings, (p) => [p.entries]);
}
